import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import {
  Search,
  Eye,
  MousePointerClick,
  GitBranch,
  AlertTriangle,
  EyeOff,
  Wrench,
  CheckCircle2,
  Clock,
  UserX,
  Code2,
  HelpCircle,
  Play,
  SkipForward,
  XCircle,
} from 'lucide-react'

/* --- Quiz Data --- */
const QUIZ = [
  {
    question: 'What is the keyboard shortcut to toggle "Show Formulas" in Google Sheets?',
    options: [
      'Ctrl + F',
      'Ctrl + `',
      'Ctrl + Shift + F',
      'Alt + F',
    ],
    correctIndex: 1,
    explanation:
      'Ctrl + ` (backtick) toggles between showing calculated values and showing the raw formulas in every cell. It\'s the first thing you should do when troubleshooting.',
  },
  {
    question: 'Why is manually entered data one of the biggest sources of problems in shared sheets?',
    options: [
      'It uses more storage space',
      'Google Sheets limits manual entries',
      'You can\'t trace it back to a source, so you can\'t validate if it\'s correct',
      'Manual data causes formulas to run slower',
    ],
    correctIndex: 2,
    explanation:
      'When data is manually entered, there\'s no formula pointing to a source. If something looks wrong, you have no way to check where the number came from or whether it\'s still correct. Only the person who typed it knows.',
  },
  {
    question: 'You see a #N/A error in a VLOOKUP. What should you check first?',
    options: [
      'Whether the sheet is shared correctly',
      'Whether the lookup value has extra spaces, typos, or is stored as text instead of a number',
      'Whether the internet connection is stable',
      'Whether the cell is formatted correctly',
    ],
    correctIndex: 1,
    explanation:
      '#N/A means the lookup value wasn\'t found. The most common causes are extra spaces (use LEN to check), typos, or a number stored as text. Use TRIM() and check with ISNUMBER() to diagnose.',
  },
  {
    question: 'What does a #REF! error typically indicate?',
    options: [
      'The formula has a typo',
      'The cell is formatted incorrectly',
      'A row, column, or sheet that the formula references has been deleted',
      'The spreadsheet needs to be refreshed',
    ],
    correctIndex: 2,
    explanation:
      '#REF! means the formula is pointing at something that no longer exists \u2014 a deleted row, column, sheet, or a broken IMPORTRANGE. Check what was recently deleted or restructured.',
  },
  {
    question: 'A SUM formula shows 500, but when you manually add the visible numbers, you get 800. What\'s the most likely cause?',
    options: [
      'The SUM function is broken',
      'Some of the values are stored as text, not numbers, so SUM ignores them',
      'The sheet has a calculation error',
      'Google Sheets rounds down automatically',
    ],
    correctIndex: 1,
    explanation:
      'SUM silently ignores text values. If numbers were pasted or manually entered as text, they look like numbers but SUM skips them. Use ISNUMBER() on suspicious cells to check.',
  },
  {
    question: 'You find a VLOOKUP with only 3 arguments (no TRUE/FALSE at the end). What\'s the risk?',
    options: [
      'It will always return #N/A',
      'It defaults to approximate match, which can silently return the wrong value',
      'It will cause a #VALUE! error',
      'It only works on sorted data',
    ],
    correctIndex: 1,
    explanation:
      'Without the 4th argument, VLOOKUP defaults to TRUE (approximate match). This can silently return a nearby value instead of an exact match \u2014 you\'ll never see an error, just wrong data.',
  },
  {
    question: 'How can you check whether a cell value was manually typed or generated by a formula?',
    options: [
      'Look at the cell colour',
      'Right-click the cell and select "Show edit history"',
      'Check the cell\'s font size',
      'Use the ISFORMULA() function',
    ],
    correctIndex: 1,
    explanation:
      'Right-click \u2192 "Show edit history" shows you exactly how and when a cell was modified. You can see whether someone typed a value, pasted it, or if it was populated by a formula.',
  },
  {
    question: 'What\'s wrong with using IFERROR(formula, 0) everywhere as a "fix"?',
    options: [
      'It slows down the spreadsheet',
      'It masks the real error \u2014 you see 0 and think it\'s real data when something is actually broken',
      'IFERROR doesn\'t work with VLOOKUP',
      'It changes the cell format to text',
    ],
    correctIndex: 1,
    explanation:
      'IFERROR hides errors by replacing them with a value. If you wrap everything in IFERROR(..., 0), broken formulas silently return 0 instead of alerting you. You end up with wrong totals and no warning.',
  },
  {
    question: 'You\'re troubleshooting a sheet and want to quickly find all cells with errors. What\'s the fastest method?',
    options: [
      'Scroll through every cell',
      'Use Ctrl + F and search for #',
      'Use conditional formatting',
      'Export to Excel and check there',
    ],
    correctIndex: 1,
    explanation:
      'All Google Sheets errors start with # (#REF!, #N/A, #VALUE!, etc.). Ctrl + F \u2192 search "#" will jump you to every error in the sheet quickly.',
  },
  {
    question: 'A formula references A2:A100, but the data now goes to row 150. What type of error is this?',
    options: [
      'A #REF! error',
      'A silent error \u2014 the formula works but misses 50 rows of data',
      'A #VALUE! error',
      'A circular reference',
    ],
    correctIndex: 1,
    explanation:
      'This is a silent error. The formula calculates fine on rows 2\u2013100, so there\'s no error message. But rows 101\u2013150 are completely ignored. Totals will be wrong with no warning. This happens when formulas aren\'t updated as data grows.',
  },
  {
    question: 'What should you do FIRST when opening someone else\'s sheet to troubleshoot it?',
    options: [
      'Start editing formulas immediately',
      'Delete all the errors',
      'Check for hidden sheets, hidden rows/columns, named ranges, and allow access on all IMPORTRANGEs',
      'Add IFERROR to every formula',
    ],
    correctIndex: 2,
    explanation:
      'Before touching anything, orient yourself. Hidden sheets and rows may contain critical calculations. Named ranges create invisible references. IMPORTRANGE cells won\'t pull data until you click "Allow access". Get the full picture first.',
  },
  {
    question: 'You find a column that\'s mostly formulas, but a few cells have manually typed values mixed in. Why is this a problem?',
    options: [
      'It looks messy',
      'Manual values won\'t update when source data changes, creating inconsistencies',
      'Google Sheets doesn\'t allow mixing formulas and values',
      'It causes the sheet to load slower',
    ],
    correctIndex: 1,
    explanation:
      'Formulas update automatically when source data changes. Manually typed values are frozen in time. When data updates, the formula cells change but the manual ones stay the same, creating mismatches that are very hard to spot.',
  },
]

/* --- Spreadsheet demo data --- */
const DEMO_SHEET_VALUES = [
  ['Offer ID', 'Offer Name', 'Clicks', 'Conversions', 'CR%'],
  ['offer_101', 'CreditCards Plus', '2,340', '47', '2.01%'],
  ['offer_123', 'InsureMe Now', '1,250', '31', '2.48%'],
  ['offer_145', 'TravelDeals Pro', '3,100', '62', '2.00%'],
  ['offer_167', 'SaveMore Bank', '890', '15', '1.69%'],
  ['offer_200', 'QuickLoans', '1,560', '39', '2.50%'],
]

const DEMO_SHEET_FORMULAS = [
  ['Offer ID', 'Offer Name', 'Clicks', 'Conversions', 'CR%'],
  ['offer_101', 'CreditCards Plus', '=IMPORTRANGE("...","Clicks!C2")', '=IMPORTRANGE("...","Conv!C2")', '=IFERROR(D2/C2,0)'],
  ['offer_123', 'InsureMe Now', '=IMPORTRANGE("...","Clicks!C3")', '31', '=IFERROR(D3/C3,0)'],
  ['offer_145', 'TravelDeals Pro', '=IMPORTRANGE("...","Clicks!C4")', '=IMPORTRANGE("...","Conv!C4")', '=IFERROR(D5/C5,0)'],
  ['offer_167', 'SaveMore Bank', '890', '=IMPORTRANGE("...","Conv!C5")', '=IFERROR(D5/C5,0)'],
  ['offer_200', 'QuickLoans', '=IMPORTRANGE("...","Clicks!C6")', '=IMPORTRANGE("...","Conv!C6")', '=IFERROR(D6/C6,0)'],
]

/* --- Show Formulas Toggle Animation --- */
function ShowFormulasDemo() {
  const [showFormulas, setShowFormulas] = useState(false)
  const data = showFormulas ? DEMO_SHEET_FORMULAS : DEMO_SHEET_VALUES

  // Problems to highlight
  const problems = showFormulas ? {
    '2-3': { color: 'red', label: 'Manual value! Not a formula' },     // D3 = "31" (manually entered)
    '4-1': { color: 'amber', label: 'Hardcoded value' },               // C5 = "890"
    '3-4': { color: 'orange', label: 'Wrong cell ref \u2014 says D5/C5 but this is row 4' },  // E4 wrong ref
    '4-4': { color: 'orange', label: 'Duplicate ref \u2014 same as row above' },               // E5 same wrong ref
  } : {}

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setShowFormulas(!showFormulas)}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium shadow-sm transition-all ${
            showFormulas
              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
              : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Code2 className="w-4 h-4" />
          {showFormulas ? 'Showing Formulas' : 'Showing Values'}
        </button>
        <span className="text-xs text-gray-400">
          Press <code className="px-1.5 py-0.5 bg-gray-100 rounded font-mono">Ctrl + `</code> in Google Sheets
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-300 shadow-sm">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b border-r border-gray-300 px-2 py-2 text-center text-xs text-gray-400 w-8" />
              {['A', 'B', 'C', 'D', 'E'].map(col => (
                <th key={col} className="border-b border-r border-gray-300 px-3 py-2 text-center text-xs font-medium text-gray-500">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, ri) => (
              <tr key={ri} className={ri === 0 ? 'bg-gray-50' : ''}>
                <td className="bg-gray-50 border-b border-r border-gray-200 text-center text-xs text-gray-400 px-2 py-2">
                  {ri + 1}
                </td>
                {row.map((cell, ci) => {
                  const problemKey = `${ri}-${ci}`
                  const problem = problems[problemKey]
                  const isFormula = showFormulas && ri > 0 && typeof cell === 'string' && cell.startsWith('=')
                  const isManual = showFormulas && ri > 0 && !cell.startsWith('=') && ci >= 2

                  return (
                    <td
                      key={ci}
                      className={`border-b border-r border-gray-200 px-3 py-2 transition-all duration-300 ${
                        ri === 0
                          ? 'font-semibold text-gray-700'
                          : problem
                            ? `bg-${problem.color}-50 border-${problem.color}-300 border-2`
                            : isFormula
                              ? 'bg-indigo-50 font-mono text-xs text-indigo-700'
                              : isManual
                                ? 'bg-red-50'
                                : 'text-gray-700'
                      }`}
                    >
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={`${showFormulas}-${ri}-${ci}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.15 }}
                        >
                          {cell}
                        </motion.div>
                      </AnimatePresence>
                      {problem && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className={`text-[10px] font-semibold text-${problem.color}-700 mt-1`}
                        >
                          {problem.label}
                        </motion.div>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {showFormulas && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="space-y-2"
          >
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3">
              <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <strong>Spotted the problems?</strong> Cell D3 has "31" typed manually instead of an IMPORTRANGE formula.
                Cell C5 has "890" hardcoded. And rows 4-5 have copy-paste errors in the CR% formula (both reference D5/C5).
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* --- Manual Data Problem Animation --- */
function ManualDataDemo() {
  const [step, setStep] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  const STEPS = [
    {
      label: 'Everything looks fine',
      description: 'The sheet shows conversions for each offer. All numbers look reasonable.',
      data: [
        { offer: 'offer_101', conv: 47, source: 'formula', updated: true },
        { offer: 'offer_123', conv: 31, source: 'manual', updated: false },
        { offer: 'offer_145', conv: 62, source: 'formula', updated: true },
      ],
      highlight: null,
      showSource: false,
    },
    {
      label: 'Source data updates overnight',
      description: 'The conversions source sheet gets new data. offer_123 now has 45 conversions, not 31.',
      data: [
        { offer: 'offer_101', conv: 47, source: 'formula', updated: true },
        { offer: 'offer_123', conv: 31, source: 'manual', updated: false },
        { offer: 'offer_145', conv: 62, source: 'formula', updated: true },
      ],
      highlight: null,
      showSource: true,
      sourceValue: 45,
    },
    {
      label: 'Formulas update automatically',
      description: 'offer_101 and offer_145 pull the latest numbers via IMPORTRANGE. But offer_123...',
      data: [
        { offer: 'offer_101', conv: 52, source: 'formula', updated: true },
        { offer: 'offer_123', conv: 31, source: 'manual', updated: false },
        { offer: 'offer_145', conv: 68, source: 'formula', updated: true },
      ],
      highlight: 1,
      showSource: true,
      sourceValue: 45,
    },
    {
      label: 'Manual value is stuck',
      description: 'offer_123 still shows 31 because someone typed it in. The real number is 45. No error, no warning \u2014 just wrong data that you\'d never know to question.',
      data: [
        { offer: 'offer_101', conv: 52, source: 'formula', updated: true },
        { offer: 'offer_123', conv: 31, source: 'manual', updated: false },
        { offer: 'offer_145', conv: 68, source: 'formula', updated: true },
      ],
      highlight: 1,
      showSource: true,
      sourceValue: 45,
      showProblem: true,
    },
  ]

  const totalSteps = STEPS.length
  const current = STEPS[step]

  useEffect(() => {
    if (!isPlaying) return
    if (step >= totalSteps - 1) {
      setIsPlaying(false)
      return
    }
    const timer = setTimeout(() => setStep(s => s + 1), 2500)
    return () => clearTimeout(timer)
  }, [step, isPlaying, totalSteps])

  const handlePlay = () => {
    setStep(0)
    setIsPlaying(true)
  }

  const handleNext = () => {
    setIsPlaying(false)
    if (step < totalSteps - 1) setStep(s => s + 1)
    else setStep(0)
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-3">
        <button
          onClick={handlePlay}
          className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700"
        >
          <Play className="w-4 h-4" />
          {step > 0 ? 'Replay' : 'Play Scenario'}
        </button>
        <button
          onClick={handleNext}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <SkipForward className="w-4 h-4" />
          {step >= totalSteps - 1 ? 'Reset' : 'Next'}
        </button>
        <span className="text-xs text-gray-400">Step {step + 1} of {totalSteps}</span>
      </div>

      {/* Progress bar */}
      <div className="flex gap-1">
        {STEPS.map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? 'bg-red-500' : 'bg-gray-200'}`} />
        ))}
      </div>

      {/* Step description */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3"
        >
          <p className="text-sm font-semibold text-blue-800">{current.label}</p>
          <p className="text-sm text-blue-700">{current.description}</p>
        </motion.div>
      </AnimatePresence>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b border-r border-gray-300 px-4 py-2 text-left text-xs font-medium text-gray-600">Offer ID</th>
              <th className="border-b border-r border-gray-300 px-4 py-2 text-right text-xs font-medium text-gray-600">Conversions</th>
              <th className="border-b border-gray-300 px-4 py-2 text-center text-xs font-medium text-gray-600">Source</th>
            </tr>
          </thead>
          <tbody>
            {current.data.map((row, i) => (
              <motion.tr
                key={row.offer}
                animate={{
                  backgroundColor: current.highlight === i
                    ? 'rgba(239, 68, 68, 0.1)'
                    : 'rgba(0,0,0,0)',
                }}
                transition={{ duration: 0.3 }}
              >
                <td className="border-b border-r border-gray-200 px-4 py-2.5 font-mono text-xs text-gray-700">
                  {row.offer}
                </td>
                <td className={`border-b border-r border-gray-200 px-4 py-2.5 text-right font-semibold ${
                  current.highlight === i ? 'text-red-600' : 'text-gray-700'
                }`}>
                  <motion.span
                    key={`${step}-${row.conv}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {row.conv}
                  </motion.span>
                  {current.showProblem && current.highlight === i && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="ml-2 text-xs text-red-500"
                    >
                      (should be {current.sourceValue})
                    </motion.span>
                  )}
                </td>
                <td className="border-b border-gray-200 px-4 py-2.5 text-center">
                  {row.source === 'formula' ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                      <CheckCircle2 className="w-3 h-3" /> formula
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                      <UserX className="w-3 h-3" /> manual
                    </span>
                  )}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Source data indicator */}
      <AnimatePresence>
        {current.showSource && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm text-gray-600"
          >
            <span className="font-semibold text-gray-700">Source sheet (Conversions):</span>{' '}
            offer_123 = <span className="font-bold text-emerald-700">{current.sourceValue}</span> conversions
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* --- Error Scanning Animation --- */
function ErrorScanDemo() {
  const [scanning, setScanning] = useState(false)
  const [scanRow, setScanRow] = useState(-1)
  const [foundErrors, setFoundErrors] = useState([])

  const SHEET_DATA = [
    { row: 2, offer: 'offer_101', clicks: '2,340', conv: '47', cr: '2.01%', error: null },
    { row: 3, offer: 'offer_123', clicks: '1,250', conv: '#N/A', cr: '#VALUE!', error: 'conv' },
    { row: 4, offer: 'offer_145', clicks: '3,100', conv: '62', cr: '2.00%', error: null },
    { row: 5, offer: 'offer_167', clicks: '#REF!', conv: '15', cr: '#REF!', error: 'clicks' },
    { row: 6, offer: 'offer_200', clicks: '1,560', conv: '39', cr: '2.50%', error: null },
    { row: 7, offer: 'offer_215', clicks: '670', conv: '0', cr: '#DIV/0!', error: 'cr' },
  ]

  const handleScan = () => {
    setScanning(true)
    setScanRow(-1)
    setFoundErrors([])

    let current = 0
    const interval = setInterval(() => {
      setScanRow(current)
      const row = SHEET_DATA[current]
      if (row.error) {
        setFoundErrors(prev => [...prev, { row: row.row, offer: row.offer, error: row.error === 'conv' ? '#N/A in Conversions' : row.error === 'clicks' ? '#REF! in Clicks' : '#DIV/0! in CR%' }])
      }
      current++
      if (current >= SHEET_DATA.length) {
        clearInterval(interval)
        setTimeout(() => setScanning(false), 300)
      }
    }, 600)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={handleScan}
          disabled={scanning}
          className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-amber-700 disabled:bg-amber-400"
        >
          <Search className="w-4 h-4" />
          {scanning ? 'Scanning...' : foundErrors.length > 0 ? 'Scan Again' : 'Ctrl + F \u2192 Search "#"'}
        </button>
        {!scanning && foundErrors.length > 0 && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-amber-700 font-semibold"
          >
            Found {foundErrors.length} errors
          </motion.span>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-300 shadow-sm">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b border-r border-gray-300 px-2 py-2 text-center text-xs text-gray-400 w-8" />
              <th className="border-b border-r border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-600">Offer ID</th>
              <th className="border-b border-r border-gray-300 px-3 py-2 text-right text-xs font-medium text-gray-600">Clicks</th>
              <th className="border-b border-r border-gray-300 px-3 py-2 text-right text-xs font-medium text-gray-600">Conversions</th>
              <th className="border-b border-gray-300 px-3 py-2 text-right text-xs font-medium text-gray-600">CR%</th>
            </tr>
          </thead>
          <tbody>
            {SHEET_DATA.map((row, i) => {
              const isScanning = scanning && scanRow === i
              const hasError = row.error !== null
              const isFound = foundErrors.some(e => e.row === row.row)

              return (
                <motion.tr
                  key={i}
                  animate={{
                    backgroundColor: isScanning
                      ? 'rgba(59, 130, 246, 0.1)'
                      : isFound && !scanning
                        ? 'rgba(245, 158, 11, 0.1)'
                        : 'rgba(0,0,0,0)',
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <td className="bg-gray-50 border-b border-r border-gray-200 text-center text-xs text-gray-400 px-2 py-2">
                    {row.row}
                  </td>
                  <td className="border-b border-r border-gray-200 px-3 py-2 font-mono text-xs text-gray-700">{row.offer}</td>
                  <td className={`border-b border-r border-gray-200 px-3 py-2 text-right ${
                    row.clicks.startsWith('#') ? 'text-red-600 font-bold' : 'text-gray-700'
                  }`}>{row.clicks}</td>
                  <td className={`border-b border-r border-gray-200 px-3 py-2 text-right ${
                    row.conv.startsWith('#') ? 'text-red-600 font-bold' : 'text-gray-700'
                  }`}>{row.conv}</td>
                  <td className={`border-b border-gray-200 px-3 py-2 text-right ${
                    row.cr.startsWith('#') ? 'text-red-600 font-bold' : 'text-gray-700'
                  }`}>{row.cr}</td>
                </motion.tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Found errors list */}
      <AnimatePresence>
        {foundErrors.length > 0 && !scanning && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2"
          >
            {foundErrors.map((err, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3 rounded-lg bg-amber-50 border border-amber-200 p-3"
              >
                <XCircle className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="text-sm text-amber-800">
                  <strong>Row {err.row}</strong> ({err.offer}): {err.error}
                </span>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* --- Data Trace Animation --- */
function DataTraceDemo() {
  const [step, setStep] = useState(0)

  // highlight = the cell you're currently inspecting (solid colour)
  // clickTarget = the cell to click next (pulsing animation to invite click)
  const TRACE_STEPS = [
    {
      id: 'output',
      label: 'Start with the suspicious value',
      formula: 'CR% shows 2.48%',
      detail: 'You\'re looking at a client report and the CR% doesn\'t look right. Click the highlighted cell to see what formula is generating this number.',
      color: 'red',
      cell: { col: 'E', row: 3 },
      grid: {
        cols: ['C', 'D', 'E'],
        headers: ['Clicks', 'Conversions', 'CR%'],
        rows: [
          { num: 2, cells: ['2,340', '47', '2.01%'] },
          { num: 3, cells: ['1,250', '31', '2.48%'], clickTarget: 2 },
          { num: 4, cells: ['3,100', '62', '2.00%'] },
        ],
      },
    },
    {
      id: 'formula',
      label: 'Read the formula in the formula bar',
      formula: '=IFERROR(D3/C3, 0)',
      detail: 'The formula bar shows =IFERROR(D3/C3, 0). This tells you CR% is calculated by dividing cell D3 (Conversions) by cell C3 (Clicks). Let\'s check D3 first \u2014 click it.',
      color: 'purple',
      cell: { col: 'E', row: 3 },
      grid: {
        cols: ['C', 'D', 'E'],
        headers: ['Clicks', 'Conversions', 'CR%'],
        rows: [
          { num: 2, cells: ['2,340', '47', '2.01%'] },
          { num: 3, cells: ['1,250', '31', '2.48%'], highlight: 2, clickTarget: 1 },
          { num: 4, cells: ['3,100', '62', '2.00%'] },
        ],
      },
    },
    {
      id: 'conv',
      label: 'Trace the numerator \u2014 Conversions',
      formula: '=IMPORTRANGE("1a2b3c...","Conversions!C3")',
      detail: 'D3 contains an IMPORTRANGE pulling from another spreadsheet. The data is coming from cell C3 on a sheet called "Conversions" in a different workbook. Now check the other input \u2014 click the Clicks cell to trace the denominator.',
      color: 'blue',
      cell: { col: 'D', row: 3 },
      grid: {
        cols: ['C', 'D', 'E'],
        headers: ['Clicks', 'Conversions', 'CR%'],
        rows: [
          { num: 2, cells: ['2,340', '47', '2.01%'] },
          { num: 3, cells: ['1,250', '31', '2.48%'], highlight: 1, clickTarget: 0 },
          { num: 4, cells: ['3,100', '62', '2.00%'] },
        ],
      },
    },
    {
      id: 'clicks',
      label: 'Trace the denominator \u2014 Clicks',
      formula: '=IMPORTRANGE("4d5e6f...","Clicks!C3")',
      detail: 'C3 is also an IMPORTRANGE, pulling from cell C3 on a "Clicks" sheet in yet another spreadsheet. This is the raw click data. Click the cell to open the source spreadsheet and verify the value.',
      color: 'green',
      cell: { col: 'C', row: 3 },
      grid: {
        cols: ['C', 'D', 'E'],
        headers: ['Clicks', 'Conversions', 'CR%'],
        rows: [
          { num: 2, cells: ['2,340', '47', '2.01%'] },
          { num: 3, cells: ['1,250', '31', '2.48%'], clickTarget: 0 },
          { num: 4, cells: ['3,100', '62', '2.00%'] },
        ],
      },
    },
    {
      id: 'source',
      label: 'Verify against the raw source',
      formula: 'Clicks!C3 = 1,250 (from BigQuery export)',
      detail: 'You\'ve reached the original data source \u2014 the BigQuery export. The Clicks sheet shows 1,250 for this offer. Now you can compare: does 1,250 match what the client report is using? If any step in this chain has a broken IMPORTRANGE, a wrong cell reference, or a manual override, you\'ll have found your problem.',
      color: 'teal',
      cell: { col: 'C', row: 3 },
      grid: {
        cols: ['A', 'B', 'C'],
        headers: ['Offer ID', 'Date', 'Clicks'],
        sheetLabel: 'Source: Clicks Sheet',
        rows: [
          { num: 2, cells: ['offer_101', '2024-01-14', '2,340'] },
          { num: 3, cells: ['offer_123', '2024-01-15', '1,250'], highlight: 2 },
          { num: 4, cells: ['offer_145', '2024-01-14', '3,100'] },
        ],
      },
    },
  ]

  const totalSteps = TRACE_STEPS.length
  const current = TRACE_STEPS[step]

  return (
    <div className="space-y-4">
      {/* Step indicators */}
      <div className="flex gap-1">
        {TRACE_STEPS.map((_, i) => (
          <button
            key={i}
            onClick={() => setStep(i)}
            className={`h-2 flex-1 rounded-full transition-colors duration-300 ${
              i <= step ? 'bg-teal-500' : 'bg-gray-200'
            } hover:opacity-80`}
          />
        ))}
      </div>

      {/* Current step card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.25 }}
          className={`rounded-xl border-2 border-${current.color}-300 bg-${current.color}-50 p-5 space-y-3`}
        >
          {/* Step number and label */}
          <div className="flex items-center gap-3">
            <span className={`flex items-center justify-center w-8 h-8 rounded-full bg-${current.color}-200 text-${current.color}-800 font-bold text-sm shrink-0`}>
              {step + 1}
            </span>
            <h4 className={`text-base font-bold text-${current.color}-900`}>
              {current.label}
            </h4>
          </div>

          {/* Formula bar with cell reference */}
          <div className="rounded-lg bg-gray-900 px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center rounded bg-gray-700 px-1.5 py-0.5 text-[10px] font-mono font-bold text-gray-300">
                {current.cell.col}{current.cell.row}
              </span>
              <div className="w-px h-3 bg-gray-700" />
              <span className="text-[10px] text-gray-500 font-mono">fx</span>
            </div>
            <code className="text-sm text-emerald-400 font-mono">{current.formula}</code>
          </div>

          {/* Mini spreadsheet showing which cell we're looking at — highlighted cells are clickable */}
          <div className="overflow-x-auto rounded-lg border border-gray-300">
            {current.grid.sheetLabel && (
              <div className="bg-gray-100 border-b border-gray-300 px-3 py-1">
                <span className="text-[10px] font-semibold text-gray-500">{current.grid.sheetLabel}</span>
              </div>
            )}
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border-b border-r border-gray-300 px-2 py-1.5 text-center text-gray-400 w-8" />
                  {current.grid.cols.map((col, ci) => (
                    <th key={ci} className="border-b border-r border-gray-300 px-3 py-1.5 text-center font-medium text-gray-500 min-w-[80px]">
                      {col}
                    </th>
                  ))}
                </tr>
                <tr className="bg-gray-50">
                  <td className="border-b border-r border-gray-200 px-2 py-1 text-center text-gray-400">1</td>
                  {current.grid.headers.map((h, ci) => (
                    <td key={ci} className="border-b border-r border-gray-200 px-3 py-1 font-semibold text-gray-600 text-center">
                      {h}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {current.grid.rows.map((row) => (
                  <tr key={row.num}>
                    <td className="bg-gray-50 border-b border-r border-gray-200 px-2 py-1.5 text-center text-gray-400">
                      {row.num}
                    </td>
                    {row.cells.map((cell, ci) => {
                      const isHighlighted = row.highlight === ci
                      const isClickTarget = row.clickTarget === ci && step < totalSteps - 1
                      return (
                        <td
                          key={ci}
                          onClick={isClickTarget ? () => setStep(step + 1) : undefined}
                          className={`border-b border-r border-gray-200 px-3 py-1.5 text-center transition-all relative ${
                            isClickTarget
                              ? `font-bold cursor-pointer border-2 border-${current.color}-400 text-${current.color}-800 bg-${current.color}-100`
                              : isHighlighted
                                ? `bg-gray-100 font-semibold text-gray-700 border border-gray-300`
                                : 'text-gray-600'
                          }`}
                        >
                          {isClickTarget && (
                            <motion.div
                              className={`absolute inset-0 rounded-sm border-2 border-${current.color}-500`}
                              animate={{ opacity: [0.3, 1, 0.3] }}
                              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                            />
                          )}
                          <span className="relative z-10">{cell}</span>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Explanation */}
          <p className={`text-sm text-${current.color}-800 leading-relaxed`}>
            {current.detail}
          </p>

          {/* Prompt to click the cell */}
          {step < totalSteps - 1 && (
            <div className={`flex items-center gap-2 rounded-lg bg-${current.color}-100 px-3 py-2`}>
              <MousePointerClick className={`w-4 h-4 text-${current.color}-600 shrink-0`} />
              <p className={`text-xs font-semibold text-${current.color}-700`}>
                Click the highlighted cell above to continue
              </p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {'\u2190'} Previous
        </button>
        <span className="text-xs text-gray-400">Step {step + 1} of {totalSteps}</span>
        <button
          onClick={() => setStep(Math.min(totalSteps - 1, step + 1))}
          disabled={step >= totalSteps - 1}
          className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Next {'\u2192'}
        </button>
      </div>

      {/* Completion message */}
      <AnimatePresence>
        {step === totalSteps - 1 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-lg bg-emerald-50 border border-emerald-200 p-4"
          >
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-800">
                <strong>That's the full trace.</strong> In practice, you'd do this for every value that looks wrong.
                Click into the cell, read the formula, follow each reference one step deeper until you reach
                the raw data. If the source data is correct but the report is wrong, the problem is somewhere
                in the chain between them.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* --- Section wrapper --- */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* --- Error reference data --- */
const VISIBLE_ERRORS = [
  { error: '#REF!', means: 'Broken reference', check: 'Deleted row/column/sheet, broken IMPORTRANGE' },
  { error: '#N/A', means: 'Lookup can\'t find value', check: 'Typos, extra spaces, number-vs-text mismatch' },
  { error: '#VALUE!', means: 'Wrong data type', check: 'Text where number expected' },
  { error: '#DIV/0!', means: 'Dividing by zero', check: 'Empty or zero denominator' },
  { error: '#ERROR!', means: 'Syntax error', check: 'Mismatched brackets, typo in formula' },
]

const SILENT_ERRORS = [
  { issue: 'Formula not dragged far enough', spot: 'New rows at the bottom aren\'t included in totals' },
  { issue: 'VLOOKUP missing FALSE', spot: 'No FALSE/0 as last argument \u2014 returns wrong value silently' },
  { issue: 'Number stored as text', spot: 'SUM ignores it, VLOOKUP fails \u2014 check with ISNUMBER()' },
  { issue: 'Extra spaces', spot: '"Zippi " \u2260 "Zippi" \u2014 check with LEN(), fix with TRIM()' },
  { issue: 'Relative vs absolute ref wrong', spot: 'Formula dragged/copied but $ signs are wrong' },
  { issue: 'IFERROR hiding real problems', spot: 'IFERROR(formula, 0) masks the error \u2014 you see 0 and think it\'s real' },
]

const DIAGNOSTIC_TRICKS = [
  { technique: 'Ctrl + `', purpose: 'See all formulas', icon: Code2 },
  { technique: 'Ctrl + F \u2192 search IMPORTRANGE', purpose: 'Find all external sources', icon: Search },
  { technique: 'Ctrl + F \u2192 search #', purpose: 'Find all visible errors', icon: AlertTriangle },
  { technique: 'Right-click \u2192 Show edit history', purpose: 'Was this typed or formula-generated?', icon: Clock },
  { technique: '=ISNUMBER(A1)', purpose: 'Is it really a number?', icon: HelpCircle },
  { technique: '=LEN(A1)', purpose: 'Hidden spaces/characters?', icon: Search },
  { technique: '=TRIM(A1)', purpose: 'Strip whitespace', icon: Wrench },
]

/* --- Main Module --- */
export default function ModuleTroubleshooting() {
  return (
    <ModuleLayout moduleId={16}>
      <div className="space-y-8">
        {/* Intro */}
        <Section delay={0}>
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">
              Troubleshooting Someone Else's Sheet
            </h2>
            <p className="text-lg text-gray-600">
              You've been handed a spreadsheet you didn't build. Something isn't working.
              This module teaches you a systematic approach to figuring out what's going
              on and fixing it.
            </p>
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
              <p className="text-sm text-blue-800">
                <strong>The golden rule:</strong> Understand before you fix. Resist the urge to
                start changing things. First, figure out how the sheet works and where
                the data comes from.
              </p>
            </div>
          </div>
        </Section>

        {/* Step 1: Orientation */}
        <Section delay={0.05}>
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold text-sm shrink-0">1</span>
              <Eye className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-bold text-gray-800">Orientation</h3>
            </div>
            <p className="ml-[52px] text-sm text-gray-600">Before touching anything, get the lay of the land.</p>
            <div className="ml-[52px] space-y-2">
              {[
                { label: 'Check for hidden sheets', detail: 'Right-click the tab bar \u2192 "Unhide". Critical calculations are often on hidden sheets.' },
                { label: 'Check for hidden rows and columns', detail: 'Look for skipped row numbers or missing column letters.' },
                { label: 'Check named ranges', detail: 'Data \u2192 Named ranges. These create invisible references that formulas use but you can\'t see in the cells.' },
                { label: 'Allow access on all IMPORTRANGEs', detail: 'IMPORTRANGE cells won\'t pull data until you click "Allow access". Do this for every one before you start investigating.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-3 rounded-lg bg-blue-50 p-3"
                >
                  <CheckCircle2 className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900">{item.label}</p>
                    <p className="text-xs text-blue-700 mt-0.5">{item.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </Section>

        {/* Step 2: Show Formulas - with interactive demo */}
        <Section delay={0.05}>
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 font-bold text-sm shrink-0">2</span>
              <Code2 className="w-5 h-5 text-indigo-500" />
              <h3 className="text-lg font-bold text-gray-800">Toggle Show Formulas</h3>
            </div>
            <p className="ml-[52px] text-sm text-gray-600">
              This is your most powerful first move. Toggle formulas on and problems jump out at you.
            </p>
            <div className="ml-[52px]">
              <ShowFormulasDemo />
            </div>
          </div>
        </Section>

        {/* Step 3: Manual Data - with animation */}
        <Section delay={0.05}>
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 text-red-700 font-bold text-sm shrink-0">3</span>
              <UserX className="w-5 h-5 text-red-500" />
              <h3 className="text-lg font-bold text-gray-800">Identify Manually Entered Data</h3>
            </div>

            <div className="ml-[52px] space-y-4">
              <div className="rounded-lg bg-red-50 border-2 border-red-200 p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm font-semibold text-red-800">
                    Manual data is one of the biggest sources of problems in shared sheets.
                  </p>
                </div>
                <p className="text-sm text-red-700">
                  If something breaks in a manually entered cell, you can't trace it back to a formula
                  or source. You just have to trust that someone typed it correctly.
                </p>
                <p className="text-sm text-red-700 font-semibold">
                  Manually entered data is very difficult for anyone other than the original person
                  to troubleshoot — because there's no formula pointing to a source, you have no way
                  to validate whether the data is correct or where it came from.
                </p>
              </div>

              <p className="text-sm font-semibold text-gray-700">Watch what happens when source data updates:</p>
              <ManualDataDemo />

              <div className="space-y-2 mt-2">
                <p className="text-sm font-semibold text-gray-800">How to identify manual data:</p>
                {[
                  { label: 'Show edit history', detail: 'Right-click a cell \u2192 "Show edit history" to see if it was typed, pasted, or populated by a formula.' },
                  { label: 'Look for inconsistencies', detail: 'Columns that are mostly formulas but have a few manually typed values mixed in \u2014 these won\'t update when source data changes.' },
                  { label: 'Common manual data problems', detail: 'Typos, inconsistent formatting, stale data that never gets refreshed, numbers entered as text.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
                    <MousePointerClick className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{item.label}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Step 4: Understand the Formula */}
        <Section delay={0.05}>
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-700 font-bold text-sm shrink-0">4</span>
              <HelpCircle className="w-5 h-5 text-purple-500" />
              <h3 className="text-lg font-bold text-gray-800">Understand the Formula</h3>
            </div>
            <div className="ml-[52px] space-y-3">
              <p className="text-sm text-gray-600">
                Click into a cell and the references light up in colour on the sheet,
                making it easy to see what feeds into the formula.
              </p>

              {/* Visual formula breakdown */}
              <div className="rounded-lg bg-gray-900 p-4 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-500 font-mono">fx</span>
                  <div className="w-px h-3 bg-gray-700" />
                </div>
                <pre className="font-mono text-sm">
                  <span className="text-gray-500">=</span>
                  <span className="text-amber-400">IFERROR</span>
                  <span className="text-gray-500">(</span>
                  <span className="text-blue-400">VLOOKUP</span>
                  <span className="text-gray-500">(</span>
                  <span className="text-green-400">A3&B3</span>
                  <span className="text-gray-500">, </span>
                  <span className="text-purple-400">Combined!A:D</span>
                  <span className="text-gray-500">, </span>
                  <span className="text-cyan-400">4</span>
                  <span className="text-gray-500">, </span>
                  <span className="text-red-400">FALSE</span>
                  <span className="text-gray-500">), </span>
                  <span className="text-amber-400">0</span>
                  <span className="text-gray-500">)</span>
                </pre>
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-400" />
                    <span className="text-gray-400">IFERROR: catches errors, returns 0</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-400" />
                    <span className="text-gray-400">VLOOKUP: searches for a value</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    <span className="text-gray-400">A3&B3: composite search key</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-400" />
                    <span className="text-gray-400">Combined!A:D: search range</span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-purple-50 border border-purple-200 p-3">
                <p className="text-sm text-purple-800">
                  <strong>Tip:</strong> Don't know what a function does? Type it into the <strong>Gemini sidebar</strong> and
                  ask "Explain this formula". It'll break it down step by step.
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* Step 5: Trace to Source - with interactive demo */}
        <Section delay={0.05}>
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-teal-100 text-teal-700 font-bold text-sm shrink-0">5</span>
              <GitBranch className="w-5 h-5 text-teal-500" />
              <h3 className="text-lg font-bold text-gray-800">Trace to Source Data</h3>
            </div>
            <div className="ml-[52px] space-y-4">
              <p className="text-sm text-gray-600">
                When a value looks wrong, you need to follow the trail of formulas backwards — from the final number
                on screen, through each formula that feeds it, all the way back to the original raw data. Here's
                exactly how you'd trace a suspicious CR% value:
              </p>
              <DataTraceDemo />

              <div className="space-y-2 mt-2">
                {[
                  { label: 'Find all IMPORTRANGE formulas', detail: 'Use Ctrl + F \u2192 search "IMPORTRANGE". Each one is an external data source. Does the source sheet still exist? Is it accessible?' },
                  { label: 'Find all QUERY formulas', detail: 'What data is being filtered or aggregated? What conditions are applied?' },
                  { label: 'Look for hidden helper columns/sheets', detail: 'Intermediate calculations are often tucked away on hidden sheets or in columns to the right of the visible data.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-lg bg-teal-50 p-3">
                    <Search className="w-4 h-4 text-teal-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-teal-900">{item.label}</p>
                      <p className="text-xs text-teal-700 mt-0.5">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Step 6: Common Errors - with scanning demo */}
        <Section delay={0.05}>
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 text-amber-700 font-bold text-sm shrink-0">6</span>
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="text-lg font-bold text-gray-800">Common Errors</h3>
            </div>

            <div className="ml-[52px] space-y-6">
              {/* Interactive error scanning */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Try it: scan for errors</h4>
                <ErrorScanDemo />
              </div>

              {/* Visible errors reference */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Visible Errors</h4>
                <div className="space-y-2">
                  {VISIBLE_ERRORS.map((err, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      className="grid grid-cols-[90px_1fr_1fr] gap-3 items-center rounded-lg border border-gray-200 p-3"
                    >
                      <code className="text-sm font-mono font-bold text-red-600">{err.error}</code>
                      <p className="text-sm text-gray-700">{err.means}</p>
                      <p className="text-xs text-gray-500">{err.check}</p>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Silent errors */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-2">Silent Errors</h4>
                <p className="text-xs text-gray-500 mb-3">
                  These are the dangerous ones — no error message, just wrong results.
                </p>
                <div className="space-y-2">
                  {SILENT_ERRORS.map((err, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-start gap-3 rounded-lg border-2 border-amber-200 bg-amber-50 p-3"
                    >
                      <EyeOff className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-amber-900">{err.issue}</p>
                        <p className="text-xs text-amber-700 mt-0.5">{err.spot}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Step 7: Diagnostic Tricks */}
        <Section delay={0.05}>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 text-green-700 font-bold text-sm shrink-0">7</span>
              <Search className="w-5 h-5 text-green-500" />
              <h3 className="text-lg font-bold text-gray-800">Quick Diagnostic Tricks</h3>
            </div>

            <div className="ml-[52px] space-y-2">
              {DIAGNOSTIC_TRICKS.map((trick, i) => {
                const TrickIcon = trick.icon
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.04 }}
                    className="flex items-center gap-4 rounded-lg bg-green-50 p-3"
                  >
                    <TrickIcon className="w-4 h-4 text-green-500 shrink-0" />
                    <code className="text-xs font-mono text-green-800 bg-green-100 px-2 py-0.5 rounded min-w-[220px]">
                      {trick.technique}
                    </code>
                    <p className="text-sm text-gray-700">{trick.purpose}</p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </Section>

        {/* Step 8: Fix Workflow */}
        <Section delay={0.05}>
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-100 text-orange-700 font-bold text-sm shrink-0">8</span>
              <Wrench className="w-5 h-5 text-orange-500" />
              <h3 className="text-lg font-bold text-gray-800">Fix Workflow</h3>
            </div>
            <div className="ml-[52px] space-y-2">
              {[
                { label: 'Fix from the source outward', detail: 'If the source data is broken, everything downstream is broken too. Start at the root.' },
                { label: 'Fix one thing at a time', detail: 'Verify each fix before moving on. Changing multiple things at once makes it impossible to know what worked.' },
                { label: 'Don\'t just IFERROR everything', detail: 'Understand WHY the error occurs first. IFERROR hides problems \u2014 it doesn\'t fix them.' },
                { label: 'Break complex formulas into helper columns', detail: 'A single monster formula is hard to debug. Split it into steps so you can see where things go wrong.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-3 rounded-lg bg-orange-50 p-3"
                >
                  <span className="text-orange-500 font-bold text-sm mt-0.5">{i + 1}.</span>
                  <div>
                    <p className="text-sm font-semibold text-orange-900">{item.label}</p>
                    <p className="text-xs text-orange-700 mt-0.5">{item.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </Section>

        {/* Step 9: Sanity Check */}
        <Section delay={0.05}>
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-bold text-sm shrink-0">9</span>
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <h3 className="text-lg font-bold text-gray-800">Sanity Check</h3>
            </div>
            <p className="ml-[52px] text-sm text-gray-600 mb-2">
              Before you call it done, run through these checks:
            </p>
            <div className="ml-[52px] space-y-2">
              {[
                'Do the totals make sense? Cross-check against a known source.',
                'Add a row of test data \u2014 do the formulas pick it up?',
                'Ctrl + F \u2192 search "#" \u2014 any errors remaining?',
              ].map((check, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 rounded-lg bg-emerald-50 p-3"
                >
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <p className="text-sm text-gray-700">{check}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </Section>

        {/* Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={16} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
